type AnyRecord = Record<string, unknown>

function asRecord(value: unknown): AnyRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as AnyRecord
}

function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value !== '') {
    const n = Number(value)

    return Number.isFinite(n) ? n : null
  }

  return null
}

function getNsDocument(metadata: unknown): AnyRecord | null {
  const m = asRecord(metadata)

  if (!m) return null
  return (asRecord(m.nsDocument) ?? asRecord(m.NsDocument)) as AnyRecord | null
}

function getDecodedData(element: AnyRecord): AnyRecord | null {
  return asRecord(element.decodedData) ?? asRecord(element.DecodedData)
}

function childElements(element: AnyRecord, decodedData: AnyRecord): unknown[] {
  const fromWrapper = asArray(element.elements) ?? asArray(element.Elements)

  if (fromWrapper && fromWrapper.length > 0) return fromWrapper

  return asArray(decodedData.elements) ?? asArray(decodedData.Elements) ?? []
}

/**
 * Every kind-specific wrapper that can carry a `base.uid`, in the camelCase/PascalCase spellings
 * the checkpoint wire uses. A text frame is serialized as a kindId-1 rectangle (uid under
 * `rectangleElement`), and any shape kind can now be a text frame too and so hold a chain uid
 * (WMPCM-7988) — a wrapper missing from this list hides its uid from the allocator, which would
 * then reissue an in-use uid and collide two chains. Legacy `textElement` and a flat `base` are
 * still handled.
 */
const UID_BEARING_WRAPPERS = [
  'textElement',
  'rectangleElement',
  'irregularPolygonElement',
  'regularPolygonElement',
  'polylineElement',
  'ovalElement',
  'groupElement',
] as const

function elementBaseUid(decodedData: AnyRecord): number {
  // A text frame is now persisted as a bare kindId-1 rectangle (base nested under
  // `rectangleElement`), not its own top-level kind — check that first alongside the legacy
  // `textElement`-nested shape, so the uid allocator's max-uid scan doesn't under-count a
  // new-format text frame's uid and risk seeding a colliding uid on next allocation.
  let inner: AnyRecord | null = null

  for (const key of UID_BEARING_WRAPPERS) {
    inner =
      asRecord(decodedData[key]) ??
      asRecord(decodedData[`${key.charAt(0).toUpperCase()}${key.slice(1)}`])

    if (inner) break
  }

  const source = inner ?? decodedData
  const base =
    asRecord(source.base) ??
    asRecord(source.Base) ??
    asRecord(decodedData.base) ??
    asRecord(decodedData.Base)

  if (!base) return 0

  return finiteNumber(base.uid ?? base.Uid) ?? 0
}

function maxUidInLayer(layer: AnyRecord): number {
  const elements = asArray(layer.elements) ?? asArray(layer.Elements) ?? []
  let max = 0

  const visit = (el: unknown): void => {
    const element = asRecord(el)

    if (!element) return

    const decodedData = getDecodedData(element)

    if (decodedData) {
      max = Math.max(max, elementBaseUid(decodedData))

      for (const child of childElements(element, decodedData)) {
        visit(child)
      }
    }
  }

  for (const el of elements) {
    visit(el)
  }

  return max
}

/**
 * Scans checkpoint metadata for the highest PageElement uid across all layers
 * (plus any persisted `spreadMetaInfo.uidSeed`). Used to seed a monotonic client-side
 * uid allocator for newly created text frames — the backend never assigns uids for
 * API-created elements (see WMPCM-6852 plan).
 */
export function readMaxUidFromCheckpointMetadata(metadata: unknown): number {
  const nsDocument = getNsDocument(metadata)

  if (!nsDocument) return 0

  const spreadMetaInfo = asRecord(nsDocument.spreadMetaInfo) ?? asRecord(nsDocument.SpreadMetaInfo)
  const uidSeed = spreadMetaInfo
    ? (finiteNumber(spreadMetaInfo.uidSeed ?? spreadMetaInfo.UidSeed) ?? 0)
    : 0

  const pasteboardElements =
    asRecord(nsDocument.pasteboardElements) ?? asRecord(nsDocument.PasteboardElements)

  let max = uidSeed

  if (pasteboardElements) {
    for (const key of Object.keys(pasteboardElements)) {
      const layer = asRecord(pasteboardElements[key])

      if (layer) max = Math.max(max, maxUidInLayer(layer))
    }
  }

  return max
}

/** Creates a monotonic uid allocator seeded from the current checkpoint's highest known uid. */
export function createUidAllocator(seed: number): () => number {
  let next = Math.max(0, Math.floor(seed))

  return () => {
    next += 1
    return next
  }
}
